import type { SimpleChange } from '@angular/core';

export interface ParsedSimpleChange {
  change: SimpleChange; // the original SimpleChange
  present: boolean; // if the SimpleChange itself is defined
  changed: boolean; // value has changed
  previousTruthy: boolean; // if the previous value was truthy
  currentTruthy: boolean; // if the current value is truthy
  toTruthy: boolean; // if value has changed from falsy to truthy
  toFalsy: boolean; // if value has changed from truthy to falsy
}

/**
 * Simplified change parser to only test if value has changed.
 *
 * @param change - param from ngOnChanges (may be undefined)
 * @param selector - callback to compare objects; set to falsy to treat them like simple types
 */
export function hasChanged(
  change: SimpleChange,
  // because SimpleChange contains stupid any type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selector: (value: any) => any = value => JSON.stringify(value)
): boolean {
  if (!change) {
    return false;
  }
  if (
    typeof selector === 'function' &&
    ((!!change.previousValue && typeof change.previousValue === 'object') ||
      (!!change.currentValue && typeof change.currentValue === 'object'))
  ) {
    return selector(change.previousValue) !== selector(change.currentValue);
  }
  return change.currentValue !== change.previousValue;
}

/**
 * Parses given SimpleChange to create a ParsedSimpleChange
 * for comfortable change handling with ngOnChanges
 *
 * @param change - param from ngOnChanges (may be undefined)
 * @param selector - callback to compare objects; set to falsy to treat them like simple types
 */
export function parseChange(
  change: SimpleChange,
  // because SimpleChange contains stupid any type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selector: (value: any) => any = value => JSON.stringify(value)
): ParsedSimpleChange {
  const present = !!change;
  const changed = hasChanged(change, selector);
  const previousTruthy = present && !!change.previousValue;
  const currentTruthy = present && !!change.currentValue;
  const toTruthy = changed && !previousTruthy && currentTruthy;
  const toFalsy = changed && previousTruthy && !currentTruthy;
  return {
    change,
    present,
    changed,
    previousTruthy,
    currentTruthy,
    toTruthy,
    toFalsy
  };
}
